import React, { useState, useEffect, Fragment } from "react";
import { AlertUnsavedData, Modal, NotificationToaster } from "components/notifications";
import PropTypes from "prop-types";
import { useDirtyData } from 'hooks/useDirtyData';
import { FormTextInput } from 'components/form-fields';
import { useValidation } from "hooks/useValidation";
import { Button, ButtonSave } from "components/buttons";
import { FleetService } from "services/FleetService";
import { Intent } from "@blueprintjs/core";
import axios from "axios";
import { CheckService } from "services/CheckService";

export function CostModal(
    {
        showModal,
        modalTitle,
        isUpdate = false,
        loading = true,
        onSave,
        onCloseModal,
        costDetails
    }
) {
    const defaultCostModel = {
        id: 0,
        name: "",
        categoryId: 1
    };

    const assetCategoryName = "asset";

    const [costTypes, setCostTypes] = useState([]);
    const [assetCategory, setAssetCategory] = useState([]);
    const [internalLoading, setInternalLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [originalCost, setOriginalCost] = useState(defaultCostModel);
    const [cost, setCost] = useState(originalCost);
    const isCostDirty = useDirtyData(originalCost, cost);
    const [isCostValid, costErrors, validateCost] = useValidation();
    const [blockModalClose, setBlockModalClose] = useState(false);

    useEffect(loadCategories, []);

    function loadCategories() {
        if (!showModal || !internalLoading) {
            return;
        }

        const costRequest = {
            requestCount: 10000,
            pageNumber: 1,
            searchTerm: null,
            filters: [{ key: "CategoryName", value: assetCategoryName }]
        };

        setInternalLoading(true);

        axios.all([
            FleetService.getCosts(costRequest),
            CheckService.getTemplateOptions()
        ])
        .then(axios.spread((costTypesResponse, templateOptionsResponse) => {
            var costTypeNames = costTypesResponse.data.map(c => c.name);
            setCostTypes(costTypeNames);

            var assetCat = templateOptionsResponse.templateCategories.find(c => c.name.toLowerCase() === assetCategoryName.toLowerCase());
            setAssetCategory(assetCat);
        }))
        .finally(() => {
            setInternalLoading(false);
        });
    }

    useEffect(loadDetails, [costDetails]);

    function loadDetails() {
        if (!showModal || !costDetails) {
            return;
        }

        const mappedCostDetails = {
            id: costDetails.id || defaultCostModel.id,
            name: costDetails.name || defaultCostModel.name,
            categoryId: assetCategory.id || defaultCostModel.categoryId,
        };

        setOriginalCost(mappedCostDetails);
        setCost(mappedCostDetails);
    }

    function onCostFieldChange(fieldName, passedValue) {
        setCost({
            ...cost,
            [fieldName]: passedValue
        });
    }

    useEffect(validate, [
        cost,
        isCostDirty,
        isCostValid,
        loading
    ]);

    function validate() {
        if (!showModal || loading) {
            return; 
        }

        let rules = [
            { fieldName: "name", required: true, maxLength: 100, notInCollection: { collection: costTypes, originalValue: originalCost.name } }
        ];

        validateCost(rules, cost);
    }

    function onSaveClick() {
        if (!isUpdate && 
            !saving &&
            (
                !isCostValid || 
                !isCostDirty
            )
        ) {
            return;
        }

        const costRequest = {
            name: cost.name,
            categoryId: cost.categoryId
        };

        setSaving(true);

        if (isUpdate) {
            FleetService.updateCostType(cost.id, costRequest)
                .then(() => {
                    NotificationToaster.show(Intent.SUCCESS, "Cost type updated successfully.");
                    onSaveComplete();
                    resetModal();
                })
                .catch(() => {
                    NotificationToaster.show(Intent.DANGER, "Could not update cost type. Please try again.");
                })
                .finally(() => {
                    setSaving(false);
                });

            return;
        }

        FleetService.createCostType(costRequest)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Cost type created successfully.");
                onSaveComplete();
                resetModal();
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not create cost type. Please try again.");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onSaveComplete() {
        resetModal();
        onSave();
    }

    function onCloseClick(forceClose = false) {
        if (isCostDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        resetModal();

        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginalCost(defaultCostModel);
        setCost(defaultCostModel);
    }
    
    return (
        <Fragment>
            <Modal 
                isOpen={showModal}
                onClose={onCloseClick}
                clickOutsideClose={true}
                title={modalTitle}
                isCloseButtonShown={false}
                updateStateOnClose={false}
            >
                <FormTextInput
                    id="input-cost-name"
                    disabled={saving}
                    loading={loading}
                    value={cost.name}
                    headingText="Cost Name"
                    placeholder="Please type the cost name here"
                    onChange={(event) => onCostFieldChange("name", event.target.value)}
                    dangerHelperText={costErrors.name}
                    large={true}
                    fill={true}
                />

                <div className="bp6-dialog-footer">
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            text="Cancel"
                            intent="none"
                            onClick={() => onCloseClick(false)}
                        />
                        <ButtonSave
                            id="add-asset-type-sub-type-button"
                            onClick={onSaveClick}
                            simpleDisabled={!isCostValid || loading || !isCostDirty}
                            text={'Save'}
                        />
                    </div>
                </div>
            </Modal>

            <AlertUnsavedData
                isDirty={isCostDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

CostModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    isUpdate: PropTypes.bool,
    loading: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    costDetails: PropTypes.object.isRequired
};